<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="flex-wrap d-flex align-center">
        <h2 class="mb-0 mr-3 black--text">{{ $t("reports.farmProductivity.title") }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t("reports.farmProductivity.aiReportGuide") }}
        </v-btn>
      </div>
      <div class="header-actions">
        <v-btn color="primary" class="mr-2" depressed @click="goToDashboard">
          {{ $t("reports.farmProductivity.backToDashboard") }}
        </v-btn>
        <v-btn color="primary" depressed @click="goToCreate">
          {{ $t("reports.farmProductivity.createNewReport") }}
        </v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined>
      <div class="flex-wrap mb-3 d-flex align-center justify-space-between">
        <h3 class="mb-2 black--text">{{ $t("reports.farmProductivity.listOfReports") }}</h3>
        <div class="d-flex align-center">
          <v-text-field
            v-model="search"
            :placeholder="$t('reports.farmProductivity.search')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 320px"
            class="mr-4"
          />
          <span class="mr-2 grey--text">{{ rangeLabel }}</span>
          <v-btn icon :disabled="page <= 1" @click="prevPage">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon :disabled="!hasNextPage" @click="nextPage">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="reports"
        :loading="loading"
        :server-items-length="total"
        hide-default-footer
        class="elevation-0"
      >
        <template v-slot:[`item.farm`]="{ item }">
          <div>{{ item.farmName || "-" }}</div>
          <div class="grey--text text-caption" v-if="item.farmId">{{ item.farmId }}</div>
        </template>
        <template v-slot:[`item.producerName`]="{ item }">
          {{ item.producerName || "-" }}
        </template>
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ item.createdAt | formatDate }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip small :color="statusColor(item.status)" dark>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="view(item)">
                <v-list-item-title>{{ $t("reports.farmProductivity.view") }}</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="item.status !== 'COMPLETED'" @click="download(item)">
                <v-list-item-title>{{ $t("reports.farmProductivity.downloadReport") }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="confirmDelete(item)">
                <v-list-item-title class="red--text">{{ $t("reports.farmProductivity.delete") }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <confirm-box
      v-if="deleteDialog"
      :dialog="deleteDialog"
      :title="$t('reports.farmProductivity.deleteConfirmTitle')"
      :message="$t('reports.farmProductivity.deleteConfirmMessage')"
      :confirm-btn-text="$t('reports.farmProductivity.delete')"
      :cancel-btn-text="$t('reports.farmProductivity.cancel')"
      @continue:action="doDelete"
      @cancel:action="deleteDialog = false"
    />
    <guide-dialog :value="showGuide" @input="showGuide = $event" />
  </div>
</template>

<script>
import FarmProductivityService from "@/_services/FarmProductivityService";
import ConfirmBox from "@/components/ConfirmBox.vue";
import GuideDialog from "./components/GuideDialog.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import downloadMixin from "@/mixins/DownloadMixin";

export default {
  name: "FarmProductivityList",
  components: { ConfirmBox, GuideDialog },
  mixins: [loadingMixin, downloadMixin],
  data() {
    return {
      reports: [],
      total: 0,
      page: 1,
      limit: 10,
      search: "",
      loading: false,
      searchTimer: null,
      deleteDialog: false,
      selected: null,
      showGuide: false,
    };
  },
  computed: {
    headers() {
      const t = (k) => this.$t(`reports.farmProductivity.${k}`);
      return [
        { text: t("reportId"), value: "reportUid", sortable: false },
        { text: t("farmAndFarmId"), value: "farm", sortable: false },
        { text: t("producerName"), value: "producerName", sortable: false },
        { text: t("dateOfReport"), value: "createdAt", sortable: false },
        { text: t("status"), value: "status", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetch();
      }, 500);
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const env = await FarmProductivityService.listReports({
          page: this.page,
          limit: this.limit,
          search: this.search || "",
        });
        const payload = (env && env.data) || {};
        this.reports = payload.reports || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          text: this.$t("reports.farmProductivity.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetch();
      }
    },
    statusColor(status) {
      return (
        {
          COMPLETED: "green",
          GENERATING: "orange",
          FAILED: "red",
        }[status] || "grey"
      );
    },
    statusLabel(status) {
      const map = {
        COMPLETED: "statusCompleted",
        GENERATING: "statusGenerating",
        FAILED: "statusFailed",
      };
      const key = map[status];
      return key ? this.$t(`reports.farmProductivity.${key}`) : status || "-";
    },
    view(item) {
      this.$router.push({ name: "farmProductivityView", params: { reportUid: item.reportUid } });
    },
    async download(item) {
      this.startLoading();
      try {
        const res = await FarmProductivityService.downloadPdf(item.reportUid);
        this.downloadBlob(res.data, `farm_productivity_${item.reportUid}.pdf`);
      } catch (e) {
        this.$notify({ text: this.$t("reports.farmProductivity.loadFailed"), type: "error" });
      } finally {
        this.stopLoading();
      }
    },
    confirmDelete(item) {
      this.selected = item;
      this.deleteDialog = true;
    },
    async doDelete() {
      this.deleteDialog = false;
      if (!this.selected) return;
      this.startLoading();
      try {
        await FarmProductivityService.deleteReport(this.selected.reportUid);
        this.$notify({ title: this.$t("success"), text: this.$t("reports.farmProductivity.deleted"), type: "success" });
        if (this.reports.length === 1 && this.page > 1) this.page -= 1;
        this.fetch();
      } catch (e) {
        this.$notify({ text: this.$t("reports.farmProductivity.loadFailed"), type: "error" });
      } finally {
        this.stopLoading();
      }
    },
    goToCreate() {
      this.$router.push({ name: "farmProductivityCreate" });
    },
    goToDashboard() {
      this.$router.push({ name: "aiReportsDashboard" });
    },
  },
};
</script>

<style scoped>
.header-actions .v-btn {
  min-width: 145px;
  height: 40px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: none;
}
</style>
